import React from "react";
import KpiCard from "../KpiCard";
import { ConfidencePill } from "../Pills";
import { fmt, fmt0, fmtInt } from "../../lib/format";
import { Activity, Gauge, Droplets, CloudRain, Container } from "lucide-react";

/**
 * Decides which confidence label to show on the tank-level card.
 * Manual reading wins; otherwise simulated; otherwise unknown so the UI
 * surfaces an honest empty state instead of a fabricated zero.
 */
function deriveTankConfidence(kpis) {
  if (kpis.latest_manual_tank_level_liters != null) return "manual";
  if (kpis.latest_simulated_tank_level_liters != null) return "simulated";
  return "unknown";
}

/**
 * Sub-label for the tank card: percent-full when computable; otherwise a
 * guided empty-state hint pointing the user to the Water page.
 */
function deriveTankSub(kpis, tankCapacity) {
  if (kpis.latest_simulated_tank_level_liters == null) {
    return "Add a tank reading on the Water page to anchor the simulation";
  }
  if (!tankCapacity) return null;
  const pct = (kpis.latest_simulated_tank_level_liters / tankCapacity) * 100;
  return `${fmt(pct, 0)}% of ${fmtInt(tankCapacity)} L`;
}

export default function WaterSection({ kpis, confidence, site }) {
  const tankConfidence = deriveTankConfidence(kpis);
  const tankSub = deriveTankSub(kpis, site.tank_capacity_liters);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h2 className="label-tiny flex items-center gap-2">
          <Droplets className="w-3.5 h-3.5 text-[#3B82F6]" /> Water
        </h2>
        <ConfidencePill level={confidence.water} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Tank level"
          value={kpis.latest_simulated_tank_level_liters != null ? fmtInt(kpis.latest_simulated_tank_level_liters) : "—"}
          unit={kpis.latest_simulated_tank_level_liters != null ? "L" : null}
          Icon={Container}
          accent="cyan"
          confidence={tankConfidence}
          sub={tankSub}
        />
        <KpiCard
          label="Autonomy"
          value={kpis.autonomy_days != null ? fmt(kpis.autonomy_days, 1) : "—"}
          unit={kpis.autonomy_days != null ? "days" : null}
          Icon={Gauge}
          accent="green"
          sub={kpis.autonomy_days != null ? "at avg daily use" : "Needs a tank anchor + daily-use estimate"}
        />
        <KpiCard label="Rain (period)" value={fmt(kpis.rain_mm, 1)} unit="mm" Icon={CloudRain} accent="blue" />
        <KpiCard
          label="Effective inflow"
          value={fmtInt(kpis.effective_rain_liters)}
          unit="L"
          Icon={Droplets}
          accent="blue"
          sub={`${fmt0(site.roof_area_m2)} m² × ${site.runoff_factor}`}
        />
        <KpiCard
          label="Consumption"
          value={kpis.consumption_liters > 0 ? fmtInt(kpis.consumption_liters) : "—"}
          unit={kpis.consumption_liters > 0 ? "L" : null}
          Icon={Activity}
          accent="cyan"
          sub={
            kpis.avg_daily_consumption_liters != null
              ? `avg ${fmtInt(kpis.avg_daily_consumption_liters)} L/day`
              : "Import a pump CSV or add manual entries"
          }
        />
      </div>
    </section>
  );
}
